import { A } from "@solidjs/router";
import { Logo } from "@storytell/core";
import {
  TbBrandDiscord,
  TbBrandFacebook,
  TbBrandInstagram,
  TbBrandLinkedin,
  TbBrandSlack,
  TbBrandTiktok,
  TbBrandYoutube,
  TbMoon,
  TbSun,
} from "solid-icons/tb";
import { For } from "solid-js";
import { useTheme } from "~/hooks/useTheme";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";

const LINK = "text-sm text-secondary-text hover:text-primary-text transition-colors";

const COLUMNS: { title: TranslationKey; links: { label: TranslationKey; href: string }[] }[] = [
  {
    title: "marketing.footer.product",
    links: [
      { label: "marketing.nav.features", href: "/features" },
      { label: "marketing.nav.pricing", href: "/pricing" },
      { label: "marketing.nav.changelog", href: "/changelog" },
      { label: "marketing.nav.compare", href: "/compare" },
      { label: "marketing.cta.download", href: "/download" },
    ],
  },
  {
    title: "marketing.footer.company",
    links: [
      { label: "marketing.nav.about", href: "/about" },
      { label: "marketing.nav.blog", href: "/blog" },
      { label: "marketing.nav.partners", href: "/partners" },
      { label: "marketing.nav.contact", href: "/contact" },
      { label: "marketing.cta.talk_to_sales", href: "/book-chief-demo" },
    ],
  },
  {
    title: "marketing.footer.legal",
    links: [
      { label: "marketing.nav.terms", href: "/terms" },
      { label: "marketing.nav.privacy", href: "/privacy" },
      { label: "marketing.nav.cookies", href: "/cookies" },
      { label: "marketing.nav.dpa", href: "/data-processing-agreement" },
      { label: "marketing.nav.delete_account", href: "/delete-account" },
    ],
  },
];

export const COMPLIANCE_BADGES: { label: TranslationKey; src: string }[] = [
  { label: "marketing.footer.badge_soc2", src: "/images/compliance/vanta-soc2.png" },
  { label: "marketing.footer.badge_hipaa", src: "/images/compliance/vanta-hipaa.png" },
  { label: "marketing.footer.badge_gdpr", src: "/images/compliance/vanta-gdpr.png" },
];

export const SOCIAL_LINKS = [
  {
    label: "Slack",
    href: "https://join.slack.com/t/storytell-playground/shared_invite/zt-26x26fkmf-YCM4rn8_qRRqChsqhDL2Gg",
    Icon: TbBrandSlack,
  },
  { label: "Discord", href: "https://discord.gg/3vmkttmddy", Icon: TbBrandDiscord },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/chief-bot/", Icon: TbBrandLinkedin },
  { label: "YouTube", href: "https://www.youtube.com/@chief_bot", Icon: TbBrandYoutube },
  { label: "Instagram", href: "https://www.instagram.com/try.chief.bot/", Icon: TbBrandInstagram },
  { label: "Facebook", href: "https://www.facebook.com/try.chief.bot", Icon: TbBrandFacebook },
  { label: "TikTok", href: "https://tiktok.com/@chief.bot", Icon: TbBrandTiktok },
];

export function MarketingFooter() {
  const { t } = useI18n();

  return (
    <footer class="mt-auto border-t border-primary-border bg-canvas">
      <div class="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div class="mb-12 grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-12">
          <div class="col-span-2 flex flex-col gap-4 sm:col-span-3 lg:col-span-3">
            <A
              href="/"
              class="inline-flex shrink-0 self-start rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              aria-label={t("marketing.nav.home")}
            >
              <Logo class="h-5 w-auto" markClass="text-accent" textClass="text-[#000D50] dark:text-primary-text" />
            </A>
            <p class="max-w-56 text-sm text-secondary-text">
              {t("marketing.footer.copyright", { year: new Date().getFullYear() })}
            </p>
            <ThemeSwitch />
          </div>

          <For each={COLUMNS}>
            {(column) => (
              <div class="lg:col-span-2">
                <h3 class="mb-3 text-sm font-semibold text-primary-text">{t(column.title)}</h3>
                <ul class="space-y-2.5">
                  <For each={column.links}>
                    {(link) => (
                      <li>
                        <A href={link.href} class={LINK}>
                          {t(link.label)}
                        </A>
                      </li>
                    )}
                  </For>
                </ul>
              </div>
            )}
          </For>

          <div class="col-span-2 flex flex-nowrap items-start gap-3 sm:col-span-3 lg:col-span-3">
            <For each={COMPLIANCE_BADGES}>
              {(badge) => (
                <a href="https://trust.chief.bot" target="_blank" rel="noopener noreferrer">
                  <img src={badge.src} alt={t(badge.label)} loading="lazy" class="h-20 w-auto" />
                </a>
              )}
            </For>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-4 border-t border-primary-border pt-6">
          <span class="mr-2 text-sm text-secondary-text">{t("marketing.footer.connect")}</span>
          <For each={SOCIAL_LINKS}>
            {(social) => (
              <a
                href={social.href}
                target="_blank"
                rel="noreferrer"
                aria-label={social.label}
                class="inline-flex items-center justify-center text-secondary-text transition-colors hover:text-primary-text"
              >
                <social.Icon class="size-4" />
              </a>
            )}
          </For>
        </div>
      </div>
    </footer>
  );
}

/**
 * Sets the theme cookie only. The settings-backed preference needs the app's
 * wire, which marketing pages don't mount, so a signed-in user's saved setting
 * can overwrite this choice on their next app load.
 */
const ThemeSwitch = () => {
  const { t } = useI18n();
  const theme = useTheme();
  const option = "inline-flex size-7 items-center justify-center rounded-md transition-colors cursor-pointer";

  return (
    <div
      role="group"
      aria-label={t("marketing.footer.theme")}
      class="inline-flex gap-0.5 self-start rounded-lg border border-primary-border p-0.5"
    >
      <button
        type="button"
        aria-label={t("marketing.footer.theme_light")}
        aria-pressed={theme.resolvedTheme() === "light"}
        class={option}
        classList={{
          "bg-panel text-primary-text": theme.resolvedTheme() === "light",
          "text-secondary-text": theme.resolvedTheme() !== "light",
        }}
        onClick={() => theme.setThemePreference("light")}
      >
        <TbSun class="size-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label={t("marketing.footer.theme_dark")}
        aria-pressed={theme.resolvedTheme() === "dark"}
        class={option}
        classList={{
          "bg-panel text-primary-text": theme.resolvedTheme() === "dark",
          "text-secondary-text": theme.resolvedTheme() !== "dark",
        }}
        onClick={() => theme.setThemePreference("dark")}
      >
        <TbMoon class="size-4" aria-hidden="true" />
      </button>
    </div>
  );
};
